import * as React from "react";
import { ChevronRight, LogOut } from "lucide-react";
import { useNavigate } from "react-router";

import { useMe } from "@/api/me";
import { Brandmark } from "@/components/layout/Brandmark";
import { TenantSwitcher } from "@/components/layout/TenantSwitcher";
import { VIEW_AS_HELP, ViewAsSelect } from "@/components/layout/Header";
import { personaLabel } from "@/components/layout/nav";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";
import { MobileBottomSheet } from "@/components/ui/mobile-bottom-sheet";
import { cn } from "@/lib/cn";
import { useCapgoVersion } from "@/native/useCapgoVersion";
import { APP_VERSION } from "@/platform";
import { useAuthStore } from "@/store/authStore";
import { useUIStore, type ThemePreference } from "@/store/uiStore";

/**
 * The 375 frame (MV2-846, app/MobileShells · app/ShellDetails).
 *
 * 48px top bar: the tenant's mark, the app name, and on the right the bell
 * and the account avatar (a SuperAdmin also keeps the compact tenant
 * switcher). The bottom nav is MobileBottomNav. The account sheet carries
 * everything today's sheet has — profile, appearance, View as (SuperAdmin
 * only, MV2-985 — shares `ViewAsSelect` with desktop's `Header.tsx` UserMenu),
 * settings, help, the build, sign out — in the board's order.
 * `ImpersonationBanner` (mounted by `AppShell` on both platforms already)
 * shows the "Viewing as …" strip while a preview is on.
 */
export function MobileTopBar(): React.ReactElement {
  const { data: me } = useMe();
  const [sheet, setSheet] = React.useState(false);
  const name = me?.full_name ?? me?.email ?? "Account";
  const initial = name.slice(0, 1).toUpperCase();
  return (
    <>
      <header className="flex h-12 flex-none items-center gap-2.5 border-b border-gray-200 bg-surface px-3.5">
        <Brandmark
          rail
          branding={me?.branding ?? null}
          loading={me === undefined}
        />
        <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-gray-900">
          {me?.branding?.app_name ?? ""}
        </span>
        {me?.primary_persona === "SuperAdmin" && <TenantSwitcher compact />}
        <NotificationsBell />
        <button
          type="button"
          onClick={() => setSheet(true)}
          aria-label="Account and settings"
          className="flex h-11 w-11 items-center justify-center"
        >
          <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-[color:var(--brand-primary)] text-[12px] font-semibold text-[color:var(--brand-primary-fg)]">
            {me?.avatar_url ? (
              <img
                src={me.avatar_url}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              initial
            )}
          </span>
        </button>
      </header>
      <AccountSheet open={sheet} onClose={() => setSheet(false)} />
    </>
  );
}

const THEMES: ThemePreference[] = ["light", "system", "dark"];

/** ops/ShellDetails "Mobile account sheet". */
export function AccountSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const authEmail = useAuthStore((s) => s.email);
  const signOutAll = useAuthStore((s) => s.signOutAll);
  const actingAs = useAuthStore((s) => s.actingAs);
  const isSuperAdmin = useAuthStore((s) => s.groups.includes("SuperAdmin"));
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const capgo = useCapgoVersion();

  const name = me?.full_name ?? authEmail ?? "Account";
  const email = me?.email ?? authEmail ?? "";
  const persona = personaLabel(me?.primary_persona);
  const tenant = actingAs?.clientName ?? me?.client?.name ?? "";
  const initial = name.slice(0, 1).toUpperCase();

  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const signOut = async () => {
    onClose();
    try {
      await signOutAll();
    } catch (err) {
      console.error("signOut failed", err);
    }
    window.location.assign("/login");
  };

  const version = capgo
    ? `v${capgo.bundleVersion}${capgo.isBuiltIn ? " · built-in" : ""}`
    : `v${APP_VERSION}`;

  return (
    <MobileBottomSheet open={open} onClose={onClose} title="Account">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 flex-none items-center justify-center overflow-hidden rounded-full bg-[color:var(--brand-primary)] text-[11px] font-semibold text-[color:var(--brand-primary-fg)]">
          {me?.avatar_url ? (
            <img
              src={me.avatar_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            initial
          )}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] font-semibold text-gray-900">
            {name}
          </span>
          <span className="type-meta truncate">
            {[email, persona, tenant].filter(Boolean).join(" · ")}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <span className="text-[12px] font-medium text-gray-800">
          Appearance
        </span>
        <span className="flex-1" />
        <div
          role="radiogroup"
          aria-label="Appearance"
          className="inline-flex gap-0.5 rounded-[8px] border border-gray-200 bg-surface-sunk p-0.5"
        >
          {THEMES.map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={theme === t}
              onClick={() => setTheme(t)}
              className={cn(
                "rounded-[6px] px-2.5 py-1 text-[12px] capitalize",
                theme === t
                  ? "bg-surface font-medium text-gray-900 shadow-xs"
                  : "text-gray-600",
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {isSuperAdmin && (
        <div className="mt-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <label htmlFor="mobile-view-as" className="text-[12px] font-medium text-gray-800">
              View as
            </label>
            <span className="flex-1" />
            <ViewAsSelect
              id="mobile-view-as"
              onPicked={onClose}
              className="h-11 rounded-[8px] border border-gray-200 bg-surface px-2.5 text-[14px] text-gray-900"
            />
          </div>
          <span className="type-meta">{VIEW_AS_HELP}</span>
        </div>
      )}

      <div className="mt-2 flex flex-col">
        <SheetRow label="Settings" onClick={() => go("/settings/my-profile")} />
        <SheetRow
          label="Help and tours"
          onClick={() => go("/getting-started")}
        />
        <SheetRow label={`App version · ${version}`} meta />
        <button
          type="button"
          onClick={() => void signOut()}
          className="flex items-center gap-2 border-t border-gray-100 py-2.5 text-[13px] text-[color:var(--danger)]"
        >
          <LogOut size={14} aria-hidden />
          Sign out
        </button>
      </div>
    </MobileBottomSheet>
  );
}

function SheetRow({
  label,
  onClick,
  meta = false,
}: {
  label: string;
  onClick?: () => void;
  meta?: boolean;
}): React.ReactElement {
  if (meta) {
    return (
      <div className="tabular flex items-center border-t border-gray-100 py-2.5 text-[13px] text-gray-600">
        <span className="flex-1">{label}</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center border-t border-gray-100 py-2.5 text-[13px] text-gray-900"
    >
      <span className="flex-1 text-left">{label}</span>
      <ChevronRight size={14} className="text-gray-500" aria-hidden />
    </button>
  );
}
